const TOKEN_KEY = "leif:token";

/**
 * Cookie-Spiegel des JWTs (SameSite=Strict): erlaubt `<video>`/`<img>`/SSE/WS
 * ohne `?token=` in der URL — Tokens landen so weder im Server-Access-Log noch
 * in Browser-Historie oder kopierten Links. Das Backend akzeptiert das Cookie
 * nur für GET/HEAD (CSRF-Schutz); Mutationen laufen weiter über den Header.
 */
const TOKEN_COOKIE = "leif_token";

function syncCookie(token: string | null): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  if (token === null) {
    document.cookie = `${TOKEN_COOKIE}=; Path=/; Max-Age=0; SameSite=Strict${secure}`;
  } else {
    document.cookie = `${TOKEN_COOKIE}=${token}; Path=/; SameSite=Strict${secure}`;
  }
}

function hasTokenCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c.startsWith(`${TOKEN_COOKIE}=`) && c.length > TOKEN_COOKIE.length + 1);
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token === null) {
    window.localStorage.removeItem(TOKEN_KEY);
  } else {
    window.localStorage.setItem(TOKEN_KEY, token);
  }
  syncCookie(token);
  window.dispatchEvent(new CustomEvent("leif:token-changed"));
}

// Upgrade-Pfad: bestehende Sessions (Token nur im localStorage) bekommen beim
// ersten Laden des neuen Bundles sofort ihren Cookie-Spiegel — ohne Re-Login.
if (typeof window !== "undefined") {
  const existing = getToken();
  if (existing) syncCookie(existing);
}

export class UnauthorizedError extends Error {
  constructor() {
    super("Unauthorized");
    this.name = "UnauthorizedError";
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (resp.status === 401) {
    setToken(null);
    throw new UnauthorizedError();
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`HTTP ${resp.status}: ${text || resp.statusText}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

export function wsUrl(path: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const url = new URL(`${proto}//${window.location.host}${path}`);
  // Cookie authentifiziert den Upgrade-Request (GET); ?token= nur als
  // Fallback, falls Cookies blockiert sind.
  if (!hasTokenCookie()) {
    const token = getToken();
    if (token) url.searchParams.set("token", token);
  }
  return url.toString();
}

/**
 * URL für EventSource / <img> / <video>. Authentifiziert normalerweise über
 * den Cookie-Spiegel (Token bleibt aus der URL raus); nur wenn kein Cookie
 * gesetzt werden konnte, fällt sie auf den ?token=-Query-Param zurück.
 */
export function tokenUrl(path: string): string {
  const u = new URL(path, window.location.origin);
  if (!hasTokenCookie()) {
    const token = getToken();
    if (token) u.searchParams.set("token", token);
  }
  return u.toString();
}

/** Multipart-Upload (FormData). fetch setzt den Content-Type selbst inkl. boundary. */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  method: "POST" | "PUT" = "POST",
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const resp = await fetch(path, { method, headers, body: form });
  if (resp.status === 401) {
    setToken(null);
    throw new UnauthorizedError();
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`HTTP ${resp.status}: ${text || resp.statusText}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}
